<script setup lang="ts">
/** All agent runs you can see: yours, or everyone's for agents admins; code-agent runs with their branch and pull request. */
import { ListChecks } from "@lucide/vue";
import { computed, h, ref } from "vue";
import { RouterLink, useRouter } from "vue-router";
import { isActive, useAgentRuns, useAgents, type AgentRun } from "@/api/agents";
import { runTitle, type AgentsCatalog } from "@/components/agents/agentMeta";
import CodeRunInfo from "@/components/agents/CodeRunInfo.vue";
import DataTable from "@/components/common/DataTable.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import StatusBadge from "@/components/common/StatusBadge.vue";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useSession } from "@/composables/useSession";
import { formatUsd } from "@/lib/format";
import type { Column } from "@/lib/table";

definePage({ meta: { title: "Agent runs", requires: { capability: "agents.read" }, layout: "wide" } });

const router = useRouter();
const { can } = useSession();
const all = ref(false);
const runs = useAgentRuns(() => ({ all: all.value, limit: 200 }));
const agents = useAgents();
const catalog = computed(() => agents.data.value as AgentsCatalog | undefined);

const columns = computed<Column<AgentRun>[]>(() => [
  {
    id: "agent", header: "Agent", accessorFn: (r) => runTitle(r, catalog.value), meta: { mobile: "title" },
    cell: ({ row }) => h(RouterLink, { to: `/agents/runs/${row.original.id}`, class: "font-medium whitespace-nowrap underline-offset-2 hover:underline", onClick: (e: Event) => e.stopPropagation() }, () => runTitle(row.original, catalog.value)),
  },
  { id: "status", header: "Status", accessorFn: (r) => r.status, meta: { mobile: "badge" }, cell: ({ row }) => h(StatusBadge, { status: row.original.status }) },
  {
    id: "goal", header: "Goal", accessorFn: (r) => r.goal, enableSorting: false, meta: { mobile: "subtitle" },
    // TableCell is nowrap: the goal wraps (two lines at most) so the columns after it stay on screen at laptop widths.
    cell: ({ row }) => h("p", { class: "line-clamp-2 max-w-xl min-w-48 whitespace-normal text-muted-foreground", title: row.original.goal }, row.original.goal),
  },
  { id: "code", header: "Branch / PR", enableSorting: false, cell: ({ row }) => h(CodeRunInfo, { run: row.original }) },
  { id: "trigger", header: "Trigger", accessorFn: (r) => r.trigger, cell: ({ row }) => h(Badge, { variant: "outline", class: "font-normal" }, () => row.original.trigger) },
  { id: "as", header: "As", accessorFn: (r) => r.username ?? "", cell: ({ row }) => h("span", { class: "text-xs" }, row.original.username ?? "—") },
  {
    id: "cost", header: "Cost", accessorFn: (r) => r.costUsd, meta: { align: "end" },
    cell: ({ row }) => h("span", { class: "text-xs tabular-nums" }, isActive(row.original) ? "…" : formatUsd(row.original.costUsd)),
  },
  {
    id: "started", header: "Started", accessorFn: (r) => r.createdAt, meta: { align: "end" },
    cell: ({ row }) => h(RelativeTime, { value: row.original.createdAt, class: "text-xs whitespace-nowrap text-muted-foreground" }),
  },
]);
</script>

<template>
  <div class="flex flex-col gap-6">
    <PageHeader title="Agent runs" description="Every run with who it ran as, why it started, what it cost — and for code agents the branch and pull request." :crumbs="[{ label: 'Agents', to: '/agents' }, { label: 'Runs' }]">
      <template #actions>
        <label v-if="can('agents.admin')" class="flex items-center gap-2 text-sm"><Switch v-model="all" /> Everyone's</label>
      </template>
    </PageHeader>
    <ListSkeleton v-if="runs.isPending.value" :rows="8" />
    <ErrorState v-else-if="runs.error.value" :error="runs.error.value" @retry="runs.refetch()" />
    <EmptyState v-else-if="!runs.data.value?.length" :icon="ListChecks" title="No runs yet" description="Start an agent from the Agents page, from a todo or from Claude Code (agent_run)." />
    <DataTable
      v-else
      :data="runs.data.value"
      :columns="columns"
      :row-key="(r) => r.id"
      row-clickable
      caption="Agent runs"
      @row-click="(r) => router.push(`/agents/runs/${r.id}`)"
    />
  </div>
</template>
